import { useMemo, useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { findCarrier } from '@hana/domain';
import { Button, Segmented, useToast } from '@hana/ui';
import { IconBell, IconBoxMultiple, IconCalendarPlus, IconChecks, IconClock, IconExternalLink, IconEye, IconMessageCircle, IconPlayerSkipForward, IconRoute, IconShip, IconSparkles, IconStar, IconUpload, IconX } from '@tabler/icons-react';
import { DESK_CARRIERS, DESK_NOTIS, DESK_REGIONS, carLabel, deskRows, filterTasks, mainCarriers, shortDue, stateCount, type CellState, type DeskAction, type DeskDock, type DeskInquiry, type DeskRateRow, type DeskTask, type QueueFilter, type RegionKey } from './desk';
import './DeskBoard.i18n';
import s from './desk.module.css';

const SEV = { 3: { label: 'dk.high', cls: s.sev3 }, 2: { label: 'dk.mid', cls: s.sev2 }, 1: { label: 'dk.low', cls: s.sev1 } } as const;
const ACT_ICON: Record<DeskAction, ReactNode> = {
  ext: <IconCalendarPlus size={16} aria-hidden="true" />,
  ask: <IconMessageCircle size={16} aria-hidden="true" />,
  enter: <IconUpload size={16} aria-hidden="true" />,
  skip: <IconPlayerSkipForward size={16} aria-hidden="true" />,
  route: <IconEye size={16} aria-hidden="true" />,
  notify: <IconBell size={16} aria-hidden="true" />,
};
const KPI_STATES = ['ok', 'soon', 'exp', 'none'] as const;
const pct = (v: number) => `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(1).replace('.', ',')}%`;

interface Props {
  rates: readonly DeskRateRow[];
  serve: Readonly<Record<string, readonly string[]>>;
  inquiries: readonly DeskInquiry[];
  tasks: readonly DeskTask[];
  job12: Readonly<Record<string, number>>;
  /** có quyền xem tiền: hiện % đổi giá và số liệu trong hàng việc */
  money: boolean;
  onOpen: (route: string, carrier: string | null) => void;
  /** khổ rộng (≥ 1800): cột phải cố định có tab Hàng việc / Thông báo và nút đóng */
  wide: boolean;
  /** iPad (layout `pad`): hàng việc nằm dưới lưới, nhóm tuyến + Hãng chủ lực do đầu trang (RatesScreen) giữ */
  pad?: boolean;
  /** điện thoại (board Mint-Rates-Phone): chỉ Hàng việc — dải thẻ số cuộn ngang + việc một cột */
  phone?: boolean;
  region?: RegionKey;
  main?: boolean;
  dock: DeskDock | null;
  onDock: (d: DeskDock | null) => void;
}

/** Bàn Pricing (board Mint-Rates): 5 thẻ số · lưới Hãng × Tuyến đang theo dõi · hàng việc Pricing. */
export function DeskBoard({ rates, serve, inquiries, tasks, job12, money, onOpen, wide, pad = false, phone = false, region: regionIn, main: mainIn, dock, onDock }: Props) {
  const t = useT();
  const toast = useToast();
  const [regionS, setRegion] = useState<RegionKey>('all');
  const [cargo, setCargo] = useState<'all' | 'RF'>('all');
  const [mainS, setMain] = useState(false);
  const region = regionIn ?? regionS;
  const main = mainIn ?? mainS;
  const [hl, setHl] = useState<CellState | 'q' | null>(null);
  const [qf, setQf] = useState<QueueFilter>('all');
  const [done, setDone] = useState<ReadonlySet<string>>(new Set());
  const [read, setRead] = useState(false);
  const pending = () => toast({ msg: t('dk.noWrite'), tone: 'err' });

  const cars = main ? mainCarriers(job12) : DESK_CARRIERS;
  const rows = useMemo(() => deskRows(rates, serve, inquiries, { region, cargo, cars }), [rates, serve, inquiries, region, cargo, cars]);
  const open = tasks.filter((x) => !done.has(x.id));
  const openInq = inquiries.filter((x) => !done.has(`inq_${x.id}`));
  const items = filterTasks(open, qf);
  const followN = Object.keys(serve).length;
  const hot = open.filter((x) => x.sev === 3).length;
  const act = (k: DeskTask, a: DeskAction) => {
    if (a === 'skip') { setDone(new Set(done).add(k.id)); toast({ msg: t('dk.skipped', { v: k.title }) }); return; }
    if (a === 'route') { onOpen(k.route, k.carrier); return; }
    pending();
  };

  const dk = pad ? 'dk.daysShort' : 'dk.days';
  const qseg = (
    <div className={s.qseg} role="group" aria-label={t('dk.qFilter')}>
                  {([['all', `${t('dk.all')} · ${open.length}`], ['hi', `${t('dk.high')} · ${hot}`], ['inq', `${t('dk.inq')} · ${open.filter((x) => x.kind === 'Hỏi giá chờ').length}`]] as const).map(([k, l]) => (
                    <button key={k} type="button" aria-pressed={qf === k} onClick={() => setQf(k)}>{l}</button>
                  ))}
                </div>
  );
  return (
    <div className={phone ? s.phone : pad ? s.pad : s.dsk}>
      <div className={s.kpis} role="group" aria-label={t('dk.statusAria')}>
        {KPI_STATES.map((k) => (
          <button key={k} type="button" className={`${s.kpi} ${s[`k_${k}`]}`} aria-pressed={hl === k} title={t('dk.hilite', { v: t(`dk.c_${k}`) })} onClick={() => setHl(hl === k ? null : k)}>
            <span className={s.kdot} aria-hidden="true" />
            <span className={s.ktx}>
              <span>{t(phone && k === 'soon' ? 'dk.c_soonS' : `dk.c_${k}`)}</span>
              <b>{stateCount(rows, k)}</b>
            </span>
            {!pad && !phone && <span className={s.ksub}>{t('dk.rateLanes')}</span>}
          </button>
        ))}
        <button type="button" className={`${s.kpi} ${s.k_q}`} aria-pressed={hl === 'q'} title={t('dk.hiliteInq')} onClick={() => setHl(hl === 'q' ? null : 'q')}>
          <span className={s.kdot} aria-hidden="true" />
          <span className={s.ktx}>
            <span>{t('dk.inqWait')}</span>
            <b>{openInq.length}</b>
          </span>
          {!pad && !phone && <span className={`${s.ksub} ${s.ksubErr}`}>{t('dk.nearDue', { n: openInq.filter((x) => x.left <= 8).length })}</span>}
        </button>
      </div>
      <div className={s.split}>
        {!phone && <section className={s.grid} aria-label={t('dk.gridAria')}>
          {pad ? (
            <div className={s.gHeadPad}>
              <span>{t('dk.followed', { n: followN })}</span>
              <span className={s.grow} />
              <a className={s.follow} href="/ezy/services" title={t('dk.followTip')}><IconStar size={16} aria-hidden="true" />{t('dk.pickFollow')}</a>
            </div>
          ) : (
          <div className={s.gHead}>
            <h2>{t('dk.gridTitle')}</h2>
            <span className={s.meta} title={t('dk.followed', { n: followN })}><IconStar size={13} aria-hidden="true" />{followN}</span>
            <span className={s.metaGrp} title={t('dk.lanesCars', { a: rows.length, b: cars.length })}>
              <span className={s.meta} title={t('dk.lanesN', { n: rows.length })}><IconRoute size={13} aria-hidden="true" />{rows.length}</span>
              <span className={s.meta} title={t('dk.carsN', { n: cars.length })}><IconShip size={13} aria-hidden="true" />{cars.length}</span>
            </span>
            <a className={s.follow} href="/ezy/services" title={t('dk.followTip')}><IconStar size={13} aria-hidden="true" />{t('dk.pickFollow')}</a>
            <span className={s.grow} />
            <Segmented kind="toggle" label={t('dk.group')} value={region} onChange={setRegion} items={DESK_REGIONS.map((r) => ({ key: r.key, label: t(`dk.r_${r.key}`) }))} />
            <Segmented kind="toggle" label={t('dk.cargoType')} value={cargo} onChange={setCargo} items={[{ key: 'all', label: 'DC + RF' }, { key: 'RF', label: t('dk.rfOnly') }]} />
            <button type="button" className={s.main} aria-pressed={main} title={t('dk.mainTip')} onClick={() => setMain(!main)}>{t('dk.main')}</button>
          </div>
          )}
          <div className={s.table} role="table" aria-label={t('dk.tableAria')}>
            <div className={s.row} role="row" style={{ gridTemplateColumns: `132px repeat(${cars.length}, minmax(0, 1fr))` }}>
              <span role="columnheader" className={s.th0}>{t('dk.lane')}</span>
              {cars.map((c) => <span key={c} role="columnheader" className={s.th}>{pad ? c : carLabel(c)}</span>)}
            </div>
            {rows.map((r) => (
              <div key={r.route} className={s.row} role="row" style={{ gridTemplateColumns: `132px repeat(${cars.length}, minmax(0, 1fr))` }}>
                <button type="button" role="rowheader" className={s.rh} title={t('dk.laneDetail', { v: r.route })} onClick={() => onOpen(r.route, null)}>
                  <b>{r.route}</b>
                  <span className={r.weak ? s.weak : undefined} title={`${t('dk.hasRate', { a: r.nOk, b: r.nServe })}${r.inq ? ` · ${t('dk.inqN', { n: r.inq })}` : ''}`}>
                    <span className={s.inl}><IconShip size={12} aria-hidden="true" />{pad ? r.nOk : `${r.nOk}/${r.nServe}`}</span>
                    {r.inq > 0 && <span className={s.inl}><IconBoxMultiple size={12} aria-hidden="true" />{r.inq}</span>}
                  </span>
                </button>
                {r.cells.map((c) => {
                  const na = c.state === 'na';
                  const up = c.delta !== undefined && c.delta > 0.5;
                  const dn = c.delta !== undefined && c.delta < -0.5;
                  const dim = !!hl && (hl === 'q' ? !c.inq : c.state !== hl);
                  const label = `${pad ? (c.car === 'ONE' ? 'ONE' : (findCarrier(c.car === 'KMTC' ? 'KMT' : c.car)?.name ?? c.car)) : carLabel(c.car)} · ${r.route} · ${c.state === 'na' ? t('dk.noServe') : t(`dk.c_${c.state}`)}${na || c.state === 'none' ? '' : c.state === 'exp' ? `${pad ? ' · ' : ' '}${t(pad ? 'dk.expDays' : 'dk.days', { n: -(c.left ?? 0) })}` : ` · ${t('dk.left', { n: c.left ?? 0 })}`}${c.delta !== undefined && (money || !pad) ? (money ? ` · ${t('dk.vsPrevPct', { v: pct(c.delta) })}` : up ? ` · ${t('dk.up')}` : dn ? ` · ${t('dk.down')}` : '') : ''}${c.inq ? ` · ${t('dk.inqWaitN', { n: c.inq })}` : ''}`;
                  return (
                    <button key={c.car} type="button" role="cell" className={`${s.cell} ${s[`c_${c.state}`]}`} disabled={na} style={{ opacity: dim ? 0.3 : undefined }} aria-label={label} title={label} onClick={() => onOpen(r.route, c.car)}>
                      <span>{na ? '·' : c.state === 'none' ? t('dk.none') : c.state === 'exp' ? `−${t(dk, { n: -(c.left ?? 0) })}` : t(dk, { n: c.left ?? 0 })}</span>
                      {c.delta !== undefined && <span aria-hidden="true" className={up ? s.up : dn ? s.down : s.flat}>{up ? '▲' : dn ? '▼' : '–'}</span>}
                      {c.inq > 0 && <span className={s.badge}>{c.inq}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className={s.legend}>
            {KPI_STATES.map((k) => (
              <span key={k}><i className={`${s.sw} ${s[`c_${k}`]}`} />{t(`dk.c_${k}`)}</span>
            ))}
            <span>▲ ▼ {t('dk.vsPrev')}</span>
            <span><i className={s.badge}>1</i>{t('dk.inqWaitLow')}</span>
          </div>
        </section>}
        {(wide ? dock !== null : true) && (
          <Dock wide={wide || phone}>
            {wide && (
              <div className={s.dockBar}>
                <div className={s.dockTabs} role="tablist" aria-label={t('dk.right')}>
                  <button type="button" role="tab" aria-selected={dock === 'q'} onClick={() => onDock('q')}>{t('dk.queue')}</button>
                  <button type="button" role="tab" aria-selected={dock === 'noti'} onClick={() => onDock('noti')}>{t('dk.noti')}</button>
                </div>
                <Button variant="ghost" size="sm" aria-label={t('dk.closePanel')} title={t('dk.closePanel')} icon={<IconX size={17} aria-hidden="true" />} onClick={() => onDock(null)} />
              </div>
            )}
            {(dock === 'noti' ? (
              <>
                {DESK_NOTIS.map((n, i) => (
                  <div key={n.text} className={s.noti} style={i ? undefined : { borderTop: 0 }}>
                    <span className={`${s.ndot} ${read ? s.ndotRead : s[`n_${n.tone}`]}`} aria-hidden="true" />
                    <div>
                      <div className={read ? s.nRead : s.nNew}>{n.text}</div>
                      <div className={s.nWhen}>{n.when}</div>
                    </div>
                  </div>
                ))}
                <button type="button" className={s.readAll} onClick={() => setRead(true)}>
                  <IconChecks size={16} aria-hidden="true" />
                  {t('dk.readAll')}
                </button>
              </>
            ) : (
              <>
          {!phone && <div className={s.qHead}>
                  <h2>{t('dk.queue')}</h2>
                  <span>{t('dk.tasksHigh', { a: open.length, b: hot })}</span>
                  {pad && qseg}
                </div>}
                <div className={s.ai} role="note">
                  <IconSparkles size={16} aria-hidden="true" />
                  <div>
                    <b>{t('dk.aiHead')}</b> {t('dk.aiBody', { n: openInq.length })}
                  </div>
                </div>
                {!pad && qseg}
                <div className={s.tasks}>
                {items.map((k) => (
                  <div key={k.id} className={s.task}>
                    <div className={s.tTop}>
                      <span className={`${s.sevChip} ${SEV[k.sev].cls}`}>{t(SEV[k.sev].label)}</span>
                      <span className={s.tKind}>{k.kind}</span>
                      {k.due && <span className={`${s.due} ${k.sev === 3 ? s.dueHot : ''}`} title={k.due}><IconClock size={13} aria-hidden="true" />{shortDue(k.due)}</span>}
                    </div>
                    <b>{money || !k.hidden ? k.title : k.hidden}</b>
                    <div className={s.tNote}>{k.note}</div>
                    <div className={s.acts}>
                      {k.acts.map((a) => (
                        <Button key={a} size="sm" variant={a === 'skip' ? 'ghost' : 'secondary'} className={a === 'skip' ? s.skipBtn : undefined} icon={ACT_ICON[a]} onClick={() => act(k, a)}>{t(phone && a === 'route' ? 'dk.a_routeP' : `dk.a_${a}`)}</Button>
                      ))}
                    </div>
                  </div>
                ))}
                </div>
                {items.length === 0 && <div className={s.qNone}>{t('dk.qNone')}</div>}
                {!pad && !phone && <a className={s.qLink} href="/ezy/inquiry"><IconExternalLink size={16} aria-hidden="true" />{t('dk.openInq', { n: openInq.length })}</a>}

              </>
            ))}
          </Dock>
        )}
      </div>
    </div>
  );
}

/** Cột phải của Bàn Pricing: khổ rộng là khối cố định không nhãn (tab ở trong), khổ nhỏ là `aside` Hàng việc Pricing. */
function Dock({ wide, children }: { wide: boolean; children: ReactNode }) {
  const t = useT();
  return wide ? <div className={s.queue}>{children}</div> : <aside className={s.queue} aria-label={t('dk.queue')}>{children}</aside>;
}
